'use client';

import { useMemo, useState } from 'react';
import {
  checkAnagram,
  DEFAULT_ANAGRAM_OPTIONS,
  type AnagramOptions,
} from '@/lib/browser-colortext-tools';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

export default function BrowserAnagramChecker() {
  const [first, setFirst] = useState('Listen');
  const [second, setSecond] = useState('Silent');
  const [options, setOptions] = useState<AnagramOptions>(DEFAULT_ANAGRAM_OPTIONS);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  const result = useMemo(() => checkAnagram(first, second, options), [first, second, options]);

  const bothEmpty = result.normalizedA.length === 0 && result.normalizedB.length === 0;

  function toggle(key: keyof AnagramOptions) {
    setOptions((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  async function handleCopy() {
    const verdict = bothEmpty
      ? 'Enter two phrases to compare.'
      : result.isAnagram
        ? `“${first}” and “${second}” are anagrams.`
        : `“${first}” and “${second}” are not anagrams.`;
    try {
      await copyText(verdict);
      setCopyMessage('Verdict copied.');
    } catch {
      setCopyMessage('The browser could not copy the verdict.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Text</span>
        <h2 className="tool-live-title">Anagram checker</h2>
        <p className="tool-live-subtitle">
          Compare two words or phrases to see if they are anagrams, with a normalized letter
          breakdown and the letters that differ.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field tool-live-field-full">
              <span>First word or phrase</span>
              <input onChange={(event) => setFirst(event.target.value)} type="text" value={first} />
            </label>
            <label className="tool-live-field tool-live-field-full">
              <span>Second word or phrase</span>
              <input
                onChange={(event) => setSecond(event.target.value)}
                type="text"
                value={second}
              />
            </label>
          </div>

          <div
            style={{
              display: 'grid',
              gap: '0.5rem',
              marginTop: '0.5rem',
            }}
          >
            <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <input
                checked={options.ignoreCase}
                onChange={() => toggle('ignoreCase')}
                type="checkbox"
              />
              <span>Ignore letter case</span>
            </label>
            <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <input
                checked={options.ignoreSpaces}
                onChange={() => toggle('ignoreSpaces')}
                type="checkbox"
              />
              <span>Ignore spaces</span>
            </label>
            <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <input
                checked={options.ignorePunctuation}
                onChange={() => toggle('ignorePunctuation')}
                type="checkbox"
              />
              <span>Ignore punctuation</span>
            </label>
          </div>

          <div className="tool-live-actions">
            <button
              className="button button-secondary"
              onClick={() => void handleCopy()}
              type="button"
            >
              Copy verdict
            </button>
          </div>
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
        </section>

        <section className="tool-live-preview">
          <div
            style={{
              padding: '1rem',
              borderRadius: '0.75rem',
              border: '1px solid rgba(148, 163, 184, 0.35)',
              marginBottom: '1rem',
            }}
          >
            <strong style={{ fontSize: '1.15rem' }}>
              {bothEmpty
                ? 'Enter two phrases to compare'
                : result.isAnagram
                  ? '✓ These are anagrams'
                  : '✕ Not anagrams'}
            </strong>
          </div>

          <div style={{ display: 'grid', gap: '0.75rem', marginBottom: '1rem' }}>
            <div>
              <span style={{ fontSize: '0.8rem', opacity: 0.7 }}>Normalized first</span>
              <div style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                {result.normalizedA || '—'}
              </div>
            </div>
            <div>
              <span style={{ fontSize: '0.8rem', opacity: 0.7 }}>Normalized second</span>
              <div style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                {result.normalizedB || '—'}
              </div>
            </div>
          </div>

          {!bothEmpty && result.diffs.length > 0 ? (
            <div>
              <strong>Letters that differ</strong>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(6rem, 1fr))',
                  gap: '0.5rem',
                  marginTop: '0.5rem',
                }}
              >
                {result.diffs.map((diff) => (
                  <div
                    key={diff.letter}
                    style={{
                      padding: '0.5rem',
                      borderRadius: '0.5rem',
                      border: '1px solid rgba(148, 163, 184, 0.35)',
                      textAlign: 'center',
                    }}
                  >
                    <div style={{ fontFamily: 'monospace', fontSize: '1.2rem' }}>{diff.letter}</div>
                    <div style={{ fontSize: '0.75rem', opacity: 0.75 }}>
                      {diff.countA} vs {diff.countB}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {!bothEmpty && result.isAnagram ? (
            <p className="tool-live-panel-note">Every letter matches in count.</p>
          ) : null}
        </section>
      </div>
    </div>
  );
}
